import { computed, onBeforeMount } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { axios } from '@/plugins';
import { useStore } from '@/store';
import {
  useLocalStorage,
  useToggle,
  useRouter,
  useToast,
  useValidateAccountActions,
} from '@base/hooks';
import { useI18n } from '@/locales';

export function useOnboardingModal(lastStep) {
  const toast = useToast();
  const router = useRouter();
  const { validateAccountActions } = useValidateAccountActions();
  const store = useStore();
  // TOGGLE_MODAL_INTRODUCTION_AUTOMATION_ENABLED
  // TODO: Remove toggle declaration
  const { isEnabled: toggleModalIntroductionAutomationEnabled } = useToggle(
    'toggleModalIntroductionAutomationEnabled'
  );
  const { t } = useI18n();

  const showModal = computed(() => store.getters['automation/getIsOnboardingModalVisible']);
  const triggerOrigin = computed(() => store.getters['automation/getOnboardingModalTriggerOrigin']);
  const account = computed(() => store.getters['info/getAccount']);
  const keyName = computed(() => `AUTOMATION_ONBOARDING_${account.value.id}`);
  const links = computed(() => store.getters['info/getLinks']);

  const showToast = () => {
    toast.info(t('automation.onboardingModal.alert'));
  };

  const debouncedShowToast = useDebounceFn(showToast, 200);

  function getLastInteractionWithModal() {
    return useLocalStorage.getLocalStorageKey(keyName.value);
  }

  function saveInteractionWithModal(step) {
    const lastValue = getLastInteractionWithModal() ?? {};

    useLocalStorage.setLocalStorageKey(
      keyName.value,
      JSON.stringify({
        step,
        createddAt: lastValue.createddAt ?? new Date(),
        updatedAt: new Date(),
      })
    );
  }

  async function initShowModal() {
    // TOGGLE_MODAL_INTRODUCTION_AUTOMATION_ENABLED
    // TODO: Remove toggleModalIntroductionAutomationEnabled from if condition
    if (!toggleModalIntroductionAutomationEnabled.value || getLastInteractionWithModal()) {
      store.dispatch('automation/closeOnboardingModal');

      return;
    }

    store.dispatch('automation/openOnboardingModal');
  }

  const handleClose = ({ currentStep }) => {
    store.dispatch('automation/closeOnboardingModal');
    saveInteractionWithModal(currentStep);

    if (triggerOrigin.value === 'help' || currentStep === lastStep) return;

    debouncedShowToast();
  };

  const handleNext = ({ currentStep }) => {
    saveInteractionWithModal(currentStep);
  };

  const handleCreateTemplate = () => {
    if (validateAccountActions()) {
      router.push({ name: 'templateSetup' });
    }
  };

  const handleCreateFormFlow = async () => {
    if (links.value.flows && validateAccountActions()) {
      const { data } = await axios.post(links.value.flows);
      router.push({ path: data.flow.links.edit });
    }
  };

  const handleCreateSpreadsheetFlow = async () => {
    if (links.value.spreadsheetFlows && validateAccountActions()) {
      const { data } = await axios.post(links.value.spreadsheetFlows);
      router.push({ path: data.links.edit });
    }
  };

  onBeforeMount(() => {
    store.dispatch('automation/closeOnboardingModal');
  });

  return {
    showModal,
    links,
    handleClose,
    handleNext,
    handleCreateTemplate,
    handleCreateFormFlow,
    handleCreateSpreadsheetFlow,
    initShowModal,
  };
}
